/**
 * Wallet reconciliation — pure logic (no UI, no Firebase).
 *
 * Compares three sources of truth for client withdrawal wallets:
 *   1. registered   — users/{uid}/crypto_wallets (what we registered)
 *   2. confirmations — users/{uid}/wallet_confirmations (what the client sent, Wallet Check)
 *   3. exchange      — Bybit withdrawal-address whitelist (live API), or any exchange
 *
 * Groups everything by address + chain so shared wallets (several clients paying
 * to the same address) and multi-account holders are treated as one group, and
 * surfaces discrepancies that could cause wrong payouts.
 */
import { validateWalletAddress } from "./cryptoWalletUtils";

/** Fiddux network label → Bybit chain identifier (mirror of functions/autoPayExchange.js). */
export const NETWORK_TO_BYBIT_CHAIN = {
  POLYGON: "MATIC",
  ETHEREUM: "ETH",
  BSC: "BSC",
  TRON: "TRX",
  BITCOIN: "BTC",
};

/** Bybit chain identifier → Fiddux network label. */
export const BYBIT_CHAIN_TO_NETWORK = Object.entries(NETWORK_TO_BYBIT_CHAIN).reduce(
  (acc, [network, chain]) => {
    acc[chain] = network;
    return acc;
  },
  {}
);

/** Bybit chain codes that use EVM (case-insensitive) addresses. */
const EVM_BYBIT_CHAINS = new Set(["MATIC", "ETH", "BSC"]);

export const CATEGORIES = {
  REGISTERED_NOT_IN_EXCHANGE: {
    id: "REGISTERED_NOT_IN_EXCHANGE",
    label: "Registered but not in exchange",
    hint: "Active in Wallet Management but missing from the exchange whitelist — auto-pay will fail until it is added.",
    severity: "high",
  },
  IN_EXCHANGE_NOT_REGISTERED: {
    id: "IN_EXCHANGE_NOT_REGISTERED",
    label: "In exchange but not registered",
    hint: "Whitelisted on the exchange but not found on any client profile — stale or wrong entry.",
    severity: "high",
  },
  CLIENT_MISMATCH: {
    id: "CLIENT_MISMATCH",
    label: "Possible cross-client mismatch",
    hint: "A client sent this address (Wallet Check) but it is registered under a different client.",
    severity: "high",
  },
  ASSET_MISMATCH: {
    id: "ASSET_MISMATCH",
    label: "Asset/chain mismatch",
    hint: "The asset or chain differs between our records and the exchange.",
    severity: "medium",
  },
  INVALID_ADDRESS: {
    id: "INVALID_ADDRESS",
    label: "Invalid address for network",
    hint: "The address format does not match its network.",
    severity: "medium",
  },
  SHARED: {
    id: "SHARED",
    label: "Shared by multiple clients",
    hint: "Same address used by more than one client/account. Usually expected — confirm it is intentional.",
    severity: "info",
  },
};

const SEVERITY_RANK = { high: 3, medium: 2, info: 1, ok: 0 };

const bybitChainForNetwork = (network) =>
  NETWORK_TO_BYBIT_CHAIN[String(network || "").toUpperCase()] || String(network || "").toUpperCase();

const normByChain = (address, bybitChain) => {
  const trimmed = (address || "").trim();
  if (!trimmed) return "";
  return EVM_BYBIT_CHAINS.has(String(bybitChain || "").toUpperCase())
    ? trimmed.toLowerCase()
    : trimmed;
};

const groupKey = (normalizedAddress, bybitChain) =>
  `${normalizedAddress}|${String(bybitChain || "").toUpperCase()}`;

/**
 * Build a Firestore-safe acknowledgement doc id from a group key.
 * Group keys look like "0xabc...|MATIC"; "|" is allowed but we keep it explicit
 * so the page and the util agree on the id format.
 */
export const ackDocIdForKey = (key) => String(key || "").replace(/\//g, "_");

/**
 * @param {{
 *   registeredWallets: Array<{ user_id, client_name, client_email, asset, network, address, wallet_type, is_active, is_default }>,
 *   confirmations: Array<{ userId, client_name, asset, network, address }>,
 *   exchangeRows: Array<{ coin, chain, address, remark, verified, status }>,
 *   acknowledgements?: Object<string, { categories?: string[], note?: string, by?: string, at?: any }>,
 *   onlyWithdrawal?: boolean,
 *   onlyActive?: boolean,
 * }}
 */
export const buildReconciliation = ({
  registeredWallets = [],
  confirmations = [],
  exchangeRows = [],
  acknowledgements = {},
  onlyWithdrawal = true,
  onlyActive = true,
} = {}) => {
  const groups = new Map();

  const ensureGroup = (key, address, bybitChain) => {
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        address,
        bybitChain,
        network: BYBIT_CHAIN_TO_NETWORK[bybitChain] || bybitChain,
        systemWallets: [],
        confirmations: [],
        exchange: null,
        exchangeRows: [],
        categories: [],
        acknowledgedCategories: [],
      });
    }
    return groups.get(key);
  };

  // 1. Registered wallets
  registeredWallets
    .filter((w) => (onlyWithdrawal ? w.wallet_type === "withdrawal" : true))
    .filter((w) => (onlyActive ? w.is_active !== false : true))
    .forEach((w) => {
      const bybitChain = bybitChainForNetwork(w.network);
      const norm = normByChain(w.address, bybitChain);
      if (!norm) return;
      const key = groupKey(norm, bybitChain);
      const g = ensureGroup(key, w.address, bybitChain);
      g.systemWallets.push(w);
    });

  // 2. Client confirmations (what the client sent)
  confirmations.forEach((c) => {
    const bybitChain = bybitChainForNetwork(c.network);
    const norm = normByChain(c.address, bybitChain);
    if (!norm) return;
    const key = groupKey(norm, bybitChain);
    const g = ensureGroup(key, c.address, bybitChain);
    g.confirmations.push(c);
  });

  // 3. Exchange whitelist — keep every row so a single address whitelisted for
  //    several coins (e.g. USDT + USDC on the same EVM address) is not a false
  //    "asset mismatch". g.exchange stays as the first row for display.
  exchangeRows.forEach((r) => {
    const bybitChain = String(r.chain || "").toUpperCase();
    const norm = normByChain(r.address, bybitChain);
    if (!norm) return;
    const key = groupKey(norm, bybitChain);
    const g = ensureGroup(key, r.address, bybitChain);
    g.exchangeRows.push(r);
    if (!g.exchange) g.exchange = r;
  });

  // Categorize each group
  const result = [];
  groups.forEach((g) => {
    const categories = [];
    const registeredClientIds = [...new Set(g.systemWallets.map((w) => w.user_id))];
    const confirmedClientIds = [...new Set(g.confirmations.map((c) => c.userId))];
    const hasSystem = g.systemWallets.length > 0;
    const hasExchange = !!g.exchange;

    if (hasSystem && !hasExchange) categories.push(CATEGORIES.REGISTERED_NOT_IN_EXCHANGE.id);
    if (!hasSystem && hasExchange) categories.push(CATEGORIES.IN_EXCHANGE_NOT_REGISTERED.id);

    // Asset/chain mismatch vs exchange. An address can be whitelisted for several
    // coins on the same chain (USDT + USDC); a blank coin means "any coin". It is
    // only a mismatch when a registered asset is not covered by ANY exchange row.
    if (hasSystem && hasExchange) {
      const exchangeCoins = new Set(
        g.exchangeRows.map((r) => String(r.coin || "").toUpperCase())
      );
      const exchangeAcceptsAnyCoin = exchangeCoins.has("");
      const assetMismatch =
        !exchangeAcceptsAnyCoin &&
        g.systemWallets.some((w) => !exchangeCoins.has(String(w.asset || "").toUpperCase()));
      if (assetMismatch) categories.push(CATEGORIES.ASSET_MISMATCH.id);
    }

    // Invalid address for its network
    const invalid = g.systemWallets.some((w) => !validateWalletAddress(w.address, w.network).valid);
    if (invalid) categories.push(CATEGORIES.INVALID_ADDRESS.id);

    // Cross-client mismatch: a client confirmed this address but did not register it,
    // while another client did register it.
    const confirmedNotRegistered = confirmedClientIds.filter((id) => !registeredClientIds.includes(id));
    if (registeredClientIds.length > 0 && confirmedNotRegistered.length > 0) {
      categories.push(CATEGORIES.CLIENT_MISMATCH.id);
    }

    // Shared across clients (expected, info)
    if (registeredClientIds.length > 1) categories.push(CATEGORIES.SHARED.id);

    // Acknowledgement: an admin can mark specific flagged categories as verified-OK,
    // with a written reason. Acknowledged categories are moved out of the active
    // list so they stop counting as outstanding issues — but stay visible/auditable.
    const ack = acknowledgements[g.key] || null;
    const ackCategories = ack ? new Set(ack.categories || []) : new Set();
    const activeCategories = categories.filter((id) => !ackCategories.has(id));
    const acknowledgedCategories = categories.filter((id) => ackCategories.has(id));

    g.allCategories = categories;
    g.categories = activeCategories;
    g.acknowledgedCategories = acknowledgedCategories;
    g.ack = ack;
    g.verified = !!ack && activeCategories.length === 0 && acknowledgedCategories.length > 0;
    g.severity = activeCategories.reduce((max, id) => {
      const sev = CATEGORIES[id]?.severity || "ok";
      return SEVERITY_RANK[sev] > SEVERITY_RANK[max] ? sev : max;
    }, activeCategories.length ? "info" : "ok");

    g.clientNames = [
      ...new Set(
        [
          ...g.systemWallets.map((w) => w.client_name || w.client_email || w.user_id),
          ...g.confirmations.map((c) => c.client_name || c.userId),
        ].filter(Boolean)
      ),
    ];
    g.registeredClientIds = registeredClientIds;

    result.push(g);
  });

  // Sort: highest severity first, then by number of categories
  result.sort((a, b) => {
    const s = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (s !== 0) return s;
    return b.categories.length - a.categories.length;
  });

  const summary = {
    total: result.length,
    registered: new Set(
      registeredWallets
        .filter((w) => (onlyWithdrawal ? w.wallet_type === "withdrawal" : true))
        .filter((w) => (onlyActive ? w.is_active !== false : true))
        .map((w) => groupKey(normByChain(w.address, bybitChainForNetwork(w.network)), bybitChainForNetwork(w.network)))
    ).size,
    inExchange: exchangeRows.length,
    matched: result.filter((g) => g.systemWallets.length > 0 && g.exchange).length,
    high: result.filter((g) => g.severity === "high").length,
    medium: result.filter((g) => g.severity === "medium").length,
    shared: result.filter((g) => (g.allCategories || g.categories).includes(CATEGORIES.SHARED.id)).length,
    verified: result.filter((g) => g.verified).length,
    byCategory: Object.keys(CATEGORIES).reduce((acc, id) => {
      acc[id] = result.filter((g) => g.categories.includes(id)).length;
      return acc;
    }, {}),
  };

  return { groups: result, summary };
};
